import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { API_URL, setTenantCurrency } from "../lib/utils";
import { loadUserLanguage } from "../i18n";

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem("orkestria_token"));
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }

        const storedToken = localStorage.getItem("orkestria_token");
        if (!storedToken) {
            setLoading(false);
            return;
        }

        // Check for portal user (magic link session)
        const portalUserStr = localStorage.getItem("orkestria_portal_user");
        if (portalUserStr) {
            try {
                const portalUser = JSON.parse(portalUserStr);
                if (portalUser.role === "client_external") {
                    // Verify the token is still valid via portal/me
                    const res = await fetch(`${API_URL}/portal/me`, {
                        headers: { Authorization: `Bearer ${storedToken}` },
                    });
                    if (res.ok) {
                        setUser(portalUser);
                        setToken(storedToken);
                        setLoading(false);
                        return;
                    } else {
                        localStorage.removeItem("orkestria_portal_user");
                        localStorage.removeItem("orkestria_token");
                        setLoading(false);
                        return;
                    }
                }
            } catch (e) {
                localStorage.removeItem("orkestria_portal_user");
            }
        }

        try {
            const response = await fetch(`${API_URL}/auth/me`, {
                headers: {
                    Authorization: `Bearer ${storedToken}`,
                },
                credentials: "include",
            });

            if (response.ok) {
                const userData = await response.json();
                setUser(userData);
                setToken(storedToken);
                // Set tenant currency
                setTenantCurrency(userData.currency);
                // Load user's language preference
                loadUserLanguage(API_URL, storedToken);
            } else {
                localStorage.removeItem("orkestria_token");
                setToken(null);
                setUser(null);
            }
        } catch (error) {
            console.error("Auth check failed:", error);
            localStorage.removeItem("orkestria_token");
            setToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        const response = await fetch(`${API_URL}/auth/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || "Login failed");
        }

        const data = await response.json();
        localStorage.setItem("orkestria_token", data.access_token);
        setToken(data.access_token);
        setUser(data.user);
        // Set tenant currency
        setTenantCurrency(data.user?.currency);
        // Load user's language preference after login
        loadUserLanguage(API_URL, data.access_token);
        return data;
    };

    const register = async (email, password, name, company_name) => {
        const response = await fetch(`${API_URL}/auth/register`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password, name, company_name }),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.detail || "Registration failed");
        }

        const data = await response.json();
        localStorage.setItem("orkestria_token", data.access_token);
        setToken(data.access_token);
        setUser(data.user);
        return data;
    };

    const loginWithGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/auth/callback";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    const handleOAuthCallback = async (sessionId) => {
        const response = await fetch(`${API_URL}/auth/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session_id: sessionId }),
            credentials: "include",
        });

        if (!response.ok) {
            throw new Error("OAuth callback failed");
        }

        const data = await response.json();
        setUser(data.user);
        // For OAuth, we rely on cookies, but also store a placeholder token
        localStorage.setItem("orkestria_token", "oauth_session");
        setToken("oauth_session");
        return data;
    };

    const logout = async () => {
        try {
            await fetch(`${API_URL}/auth/logout`, {
                method: "POST",
                credentials: "include",
            });
        } catch (error) {
            console.error("Logout error:", error);
        }

        localStorage.removeItem("orkestria_token");
        localStorage.removeItem("orkestria_portal_user");
        setToken(null);
        setUser(null);
    };

    const setPortalSession = (accessToken, portalUser) => {
        localStorage.setItem("orkestria_token", accessToken);
        localStorage.setItem("orkestria_portal_user", JSON.stringify(portalUser));
        setToken(accessToken);
        setUser(portalUser);
    };

    const value = {
        user,
        token,
        loading,
        isAuthenticated: !!user,
        isPortalClient: user?.role === "client_external",
        login,
        register,
        loginWithGoogle,
        handleOAuthCallback,
        logout,
        checkAuth,
        setPortalSession,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
